import { useMemo } from 'react';

import { useAccountStore } from '@/store/use-account-store';
import { useCapitalPlans } from '@/features/vest-capital/api/get-capital-plans';
import useFundedAccount from '@/hooks/use-funded-account';
import { CapitalStatus } from '@/types';
import { getIsStatusEval } from '@/features/vest-capital/utils/status';
import { centsToDollars } from '@/utils/value-format';

type UseCapitalAccountDataOptions = {
  address?: string;
};

export const useCapitalAccountData = (
  options: UseCapitalAccountDataOptions = {},
) => {
  const address = useAccountStore((state) => state.address);
  const { data: capitalPlans } = useCapitalPlans();
  const { isEval, isFundedOrEval, capitalStatusData } = useFundedAccount();
  const resolvedAddress = options.address ?? address;

  const {
    capitalStatus,
    accountSize,
    goalSize,
    isEvalForAddress,
    isFundedOrEvalForAddress,
  } = useMemo(() => {
    if (!resolvedAddress || !capitalStatusData) {
      return {
        capitalStatus: null,
        accountSize: 0,
        goalSize: 0,
        isEvalForAddress: undefined,
        isFundedOrEvalForAddress: undefined,
      };
    }

    const capitalData = capitalStatusData[resolvedAddress.toLowerCase()];

    if (!capitalData) {
      return {
        capitalStatus: null,
        accountSize: 0,
        goalSize: 0,
        isEvalForAddress: undefined,
        isFundedOrEvalForAddress: undefined,
      };
    }

    const plan = capitalPlans?.find(
      (plan) => plan.name === capitalData.active_capital_plan,
    );
    const isFundedOrEvalForAddress = ![
      CapitalStatus.UNKNOWN,
      CapitalStatus.NOT_ELIGIBLE,
    ].includes(
      capitalData.capital_status
    );

    return {
      capitalStatus: capitalData.capital_status,
      accountSize: centsToDollars(plan?.metadata?.account_size ?? '0'),
      goalSize: centsToDollars(plan?.metadata?.step_1_goal ?? '0'),
      isEvalForAddress: getIsStatusEval(capitalData.capital_status),
      isFundedOrEvalForAddress: isFundedOrEvalForAddress,
    };
  }, [resolvedAddress, capitalPlans, capitalStatusData]);


  return {
    isEval: options.address ? isEvalForAddress ?? false : isEval,
    isFundedOrEval: options.address
      ? isFundedOrEvalForAddress ?? false
      : isFundedOrEval,
    capitalStatus,
    accountSize,
    goalSize,
  };
};
